import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useState, type ReactElement } from "react";
import type { TaxOperationListResult, TaxSimulationResult } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { formatBRL } from "@cx/domain";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { estimateBody, type EstimateInput } from "./tax-estimate";
import { taxTotalsRows } from "./tax-labels";

/** Espera a digitação assentar antes de simular (a prévia do pedido muda a cada tecla). */
const DEBOUNCE_MS = 400;

function useDebounced<T>(value: T, ms: number): T {
  const key = JSON.stringify(value);
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(JSON.parse(key) as T), ms);
    return () => clearTimeout(t);
  }, [key, ms]);
  return settled;
}

/** Totais da simulação (só os valores diferentes de zero) e o total da nota. */
export function TaxTotalsList({ result }: { result: TaxSimulationResult }): ReactElement {
  const rows = taxTotalsRows(result.totals);
  return (
    <dl className="row mb-0 small" data-testid="tax-totals">
      {rows.map((r) => (
        <Fragment key={r.label}>
          <dt className="col-7 fw-normal">{r.label}</dt>
          <dd className="col-5 text-end mb-1">{formatBRL(r.cents)}</dd>
        </Fragment>
      ))}
      <dt className="col-7">Total da nota</dt>
      <dd className="col-5 text-end fw-bold mb-1">{formatBRL(result.totals.invoiceCents)}</dd>
      {result.totals.approxTaxCents > 0 && (
        <>
          <dt className="col-7 fw-normal">Tributos aproximados (Lei 12.741)</dt>
          <dd className="col-5 text-end mb-0">{formatBRL(result.totals.approxTaxCents)}</dd>
        </>
      )}
    </dl>
  );
}

/**
 * "Impostos estimados" do pedido e da proposta (T2.2): somente leitura, pelo motor fiscal com a natureza escolhida
 * (vazio = a padrão do cliente ou da empresa). Itens sem produto ficam de fora (sem NCM) com a parte deles do desconto,
 * frete e outras despesas (`estimateBody`); uma simulação por pausa na digitação. Some sem tax_operations:read.
 */
export function TaxEstimatePanel(p: EstimateInput): ReactElement | null {
  const { companyId, me, api } = useSession();
  const allowed = can(me.permissions as Permissions, "tax_operations", "read");
  const [operationId, setOperationId] = useState("");
  const ops = useQuery({
    queryKey: ["tax-operations", companyId, "picker"],
    enabled: allowed,
    queryFn: () => api<TaxOperationListResult>("/tax-operations?status=active&limit=100"),
  });
  const body = useDebounced(estimateBody(p, operationId), DEBOUNCE_MS);
  const sim = useQuery({
    queryKey: ["tax-operations", companyId, "estimate", body],
    enabled: allowed && !!body,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: () => api<TaxSimulationResult>("/tax/simulate", { method: "POST", body: JSON.stringify(body) }),
  });
  if (!allowed) return null;
  const outs = (ops.data?.data ?? []).filter((o) => o.direction === "out");
  return (
    <section className="box card p-3 mb-3" aria-labelledby="tax-estimate" aria-live="polite">
      <h2 id="tax-estimate" className="h6">
        Impostos estimados
      </h2>
      <label htmlFor="tax-estimate-op" className="form-label small mb-1">
        Natureza de operação
      </label>
      <select
        id="tax-estimate-op"
        className="form-select form-select-sm mb-2"
        value={operationId}
        onChange={(e) => setOperationId(e.target.value)}
      >
        <option value="">Padrão do cliente ou da empresa</option>
        {outs.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      {!body && <p className="text-muted small mb-0">Escolha o cliente e inclua produtos.</p>}
      {body && sim.error && (
        <div className="alert alert-warning py-2 small mb-0" role="alert">
          {errorMessage(sim.error)}
        </div>
      )}
      {body && sim.data && !sim.error && (
        <div aria-busy={sim.isFetching}>
          <p className="small text-muted mb-2">
            {sim.data.operation.name} · CFOP {[...new Set(sim.data.items.map((i) => i.cfop))].join(", ")}
          </p>
          <TaxTotalsList result={sim.data} />
          <p className="form-text mb-0">Estimativa: os valores da nota são calculados na emissão.</p>
        </div>
      )}
    </section>
  );
}
